import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, map, Observable, of, shareReplay, take, throwError } from 'rxjs';
import { Logger } from 'src/app/@common/log/logger';
import { Page } from '../../../@common/entities/page.interface';
import { Language, PublishedStatus, SortOrder, V2VideoPartsResource, V2VideosResource, Video, VideoInfo, VideoInfoSort, VideoPartInfo, VideoPartInfoSort, VideoType } from '../../../@generated';
import { NavigationService } from '../../navigation/application/navigation.service';
import { UserService } from '../../user/application/user.service';
import { switchMap } from 'rxjs/operators';
import { SyncedStatus } from 'src/app/@generated/model/synced-status';

@Injectable({
    providedIn: 'root',
})
export class VideoService {
    private userService = inject(UserService);
    private videoResource = inject(V2VideosResource);
    private videoPartsResource = inject(V2VideoPartsResource);
    private logger = inject(Logger);
    private navigationService = inject(NavigationService);

    public getVideos(
        page: number,
        pageSize: number,
        order: SortOrder,
        sort: VideoInfoSort | undefined,
        language: Language | undefined,
        type: VideoType | undefined,
        year: number | undefined,
        authorId: number | undefined
    ): Observable<Page<VideoInfo>> {
        return this.videoResource.getVideosInfos(page, pageSize, order, sort, type, year, language)
            .pipe(
                map((response) => {
                    if (authorId !== undefined) {
                        // Filter the videos based on the authorId by checking if any author in the authors list matches
                        response.results = response.results.filter(video =>
                            video.authors.some(author => author.id === authorId)
                        );
                    }
                    return response;
                }),
                shareReplay(1),
                catchError(e => {
                    this.logger.apiError('Get videos failed', e);
                    return of({ count: 0, results: [] });
                })
            );
    }



    public getLatestVideos(): Observable<Page<VideoInfo>> {
        return this.videoResource.getVideosInfos(1, 4, SortOrder.Descending, VideoInfoSort.Date).pipe(
            shareReplay(1),
            catchError(e => {
                this.logger.apiError('Get latest videos failed', e);
                return of({ count: 0, results: [] });
            })
        );
    }

    public getVideoInfos(
        page: number,
        pageSize: number,
        sort: VideoInfoSort | undefined,
        order: SortOrder | undefined,
        query: string | undefined,
        status: PublishedStatus | undefined
    ): Observable<Page<VideoInfo>> {
        return this.videoResource.getVideosInfos(page, pageSize, order, sort, undefined, undefined, undefined, query, status)
            .pipe(
                shareReplay(1),
                catchError(e => {
                    this.logger.apiError('Get videos failed', e);
                    return of({ count: 0, results: [] });
                })
            );
    }

    public getMostViewedVideos(): Observable<VideoInfo[]> {
        return this.videoResource.getVideosInfos(1, 4, SortOrder.Descending, VideoInfoSort.TotalViews).pipe(
            map(response => response.results),
            catchError(e => {
                this.logger.apiError('Get most viewed videos failed', e);
                return of([]);
            })
        );
    }

    public getPrivateVideoInfos(): Observable<VideoInfo[]> {
        return this.videoResource.getVideosInfos(
            1,
            999999,
            SortOrder.Ascending,
            VideoInfoSort.Title,
            undefined,
            undefined,
            undefined,
            undefined,
            PublishedStatus.Private
        ).pipe(
            map(response => response.results),
            shareReplay(1),
            catchError(e => {
                this.logger.apiError('Get private video infos failed', e);
                return of([]);
            })
        );
    }

    public getVideoBySlug(slug: string): Observable<Video> {
        return this.videoResource.getVideoBySlug(slug).pipe(
            shareReplay(1),
            catchError(e => {
                if (e.status === 403) {
                    this.navigationService.navigateToUnauthorized();
                } else if (e.status === 404) {
                    this.navigationService.navigateToNotFound();
                } else {
                    this.logger.apiError('Get video by id failed', e);
                }

                return EMPTY;
            })
        )
    }

    public getVideoWithPrivateParts(id: number): Observable<Video> {
        return this.videoResource.getVideoById(id).pipe(
            shareReplay(1),
            catchError(e => {
                if (e.status === 403) {
                    this.navigationService.navigateToUnauthorized();
                } else if (e.status === 404) {
                    this.navigationService.navigateToNotFound();
                } else {
                    this.logger.apiError('Get video by id failed', e);
                }

                return throwError(() => e);
            })
        );
    }

    public addView(id: number) {
        this.videoResource.incrementVideoView(id, {
            user_id: this.userService.user()?.id.toString() ?? this.userService.getAnonymousUserId(),
            authenticated: this.userService.isAuthenticated()
        }).pipe(
            take(1),
            catchError(e => {
                this.logger.apiError(`Add view for videoSlug: ${id} failed`, e);
                return EMPTY;
            })
        ).subscribe()
    }

    public getWatchHistory(page: number, pageSize: number): Observable<Page<VideoInfo>> {
        return this.videoResource.getVideoHistory(page, pageSize).pipe(
            shareReplay(1),
            catchError(e => {
                this.logger.apiError('Get history failed', e);
                throw throwError(() => e);
            })
        );
    }

    public addHistory(videoId: number, start: number, end: number): void {
        this.videoResource.addVideoToHistory(videoId, {
            start_timestamp: start,
            end_timestamp: end
        }).pipe(
            take(1),
            catchError(e => {
                this.logger.apiError('Add history failed', e);
                throw throwError(() => e);
            })
        ).subscribe();
    }

    public getUserUploadedVideos(limit: number, page: number): Observable<Page<VideoInfo>> {
        return this.videoResource.getUserUploadedVideos(limit, page).pipe(
            shareReplay(1),
            catchError(e => {
                this.logger.apiError('Get user uploaded videos failed', e);
                return of({ count: 0, results: [] });
            })
        );
    }

    public getVideoPartInfos(
        page: number,
        pageSize: number,
        sort: VideoPartInfoSort | undefined,
        order: SortOrder | undefined,
        query: string | undefined,
        status: PublishedStatus | undefined,
        sync: SyncedStatus | undefined
    ): Observable<Page<VideoPartInfo>> {
        return this.videoPartsResource.getVideoPartsInfos(page, pageSize, order, sort, query, status, sync) // Pass synced here
            .pipe(
                shareReplay(1),
                catchError(e => {
                    this.logger.apiError('Get video parts failed', e);
                    return of({ count: 0, results: [] });
                })
            );
    }    

    public logError(message: string, error: any): void {
        this.logger.apiError(message, error);
    }

    public getAllVideos(): Observable<VideoInfo[]> {
            return this.videoResource.getVideosInfos(1, 100, SortOrder.Ascending, VideoInfoSort.Date, undefined)
                .pipe(
                    map(response => response.results),
                    catchError(e => {
                        this.logger.apiError('Get all user infos failed', e);
                        return of([]);
                    })
                );
        }
}